import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { IMfaApiResponse, IMfaSetupData } from './mfaTypes';

interface IRequestDisableMfaInput {
  token?: string;
}

const initialState: IApiState<IMfaApiResponse<IMfaSetupData>> = {
  value: null,
  status: 'idle',
  error: '',
};

export const requestDisableMfaAsync = createAsyncThunk(
  'settings/mfa/request-disable',
  async (
    { token }: IRequestDisableMfaInput,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const { data } = await api.post<IMfaApiResponse<IMfaSetupData>>(
        '/settings/mfa/request-disable',
        token ? { token } : {},
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'X-Business-Id': businessId,
          },
        },
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IMfaApiResponse<IMfaSetupData>>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(showAlert({ status: 'error', description: errorMessage }));

      return rejectWithValue(error.response?.data);
    }
  },
);

export const requestDisableMfaSlice = createSlice({
  name: 'settings/mfa/request-disable',
  initialState,
  reducers: {
    resetRequestDisableMfa: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestDisableMfaAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(requestDisableMfaAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(requestDisableMfaAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IMfaApiResponse<IMfaSetupData>;
        state.error = action.error.message;
      });
  },
});

export const { resetRequestDisableMfa } = requestDisableMfaSlice.actions;

export default requestDisableMfaSlice.reducer;
